import React from 'react';
import { Box, Divider, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    faArchive,
    faArrowsRotate,
    faBriefcase,
    faCheck,
    faCheckSquare,
    faClock,
    faExternalLinkAlt,
    faFileAlt,
    faPlay,
    faTimes,
    faTrash,
    faUndo,
    faEye,
    faBan,
    faThumbtack,
} from '@fortawesome/free-solid-svg-icons';
import { SourceDocumentState } from './WorkItemDrawerHeader';
import AiMonthlyCreditsSummary from './AiMonthlyCreditsSummary';
import type { EntitlementSnapshot } from '../api/subscription';

type ActionHandler = () => Promise<unknown> | unknown;

type RunAction = (action: ActionHandler) => Promise<void>;

interface WorkItemDrawerActionsMenuProps {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    saving: boolean;
    isDoneState: boolean;
    isTriageState: boolean;
    hasActiveSnooze: boolean;
    isArchivedState: boolean;
    hasDocument: boolean;
    sourceDocumentState: SourceDocumentState;
    onRunAction: RunAction;
    onReopen: ActionHandler;
    onMarkDone: ActionHandler;
    onStart: ActionHandler;
    onDismiss: ActionHandler;
    onUnsnooze: ActionHandler;
    onSnooze: (days: number) => Promise<unknown> | unknown;
    onClassifyApplication: ActionHandler;
    onClassifyTask: ActionHandler;
    /** Absent when it would reopen the view the user is already in. */
    onOpenInWorkbench?: ActionHandler;
    onOpenSourceDocument: ActionHandler;
    onToggleArchive: ActionHandler;
    onRequestDelete: () => void;
    onRerunEnrichment: ActionHandler;
    enrichRerunBusy: boolean;
    aiCreditsBootstrapBlockingLoad: boolean;
    aiCreditsBootstrapError: string | null;
    aiCreditsEntitlements: EntitlementSnapshot | null | undefined;
    showSourceRuleActions?: boolean;
    currentSourceRuleAttention?: 'action' | 'watch' | 'ignore' | null;
    onKeepAsTask?: ActionHandler;
    onWatchSource?: ActionHandler;
    onIgnoreSource?: ActionHandler;
    /** Undefined when the host provides no list navigation (toggle is then hidden). */
    autoAdvanceEnabled?: boolean;
    onToggleAutoAdvance?: () => void;
    /** Source home or virtual source overlay: no Mark complete. */
    blockComplete?: boolean;
    /** Start and Dismiss already sit in the verb bar. Defaults to `blockComplete`. */
    hideTriageVerbs?: boolean;
    /** Virtual source overlay: the loaded row is still the child, so hide Reopen. */
    allowReopen?: boolean;
    /**
     * Virtual source overlay (D6): only Open source document. The child's snooze,
     * archive and delete stay out of reach while the title is the source.
     */
    sourceDocumentOnly?: boolean;
}

const WorkItemDrawerActionsMenu: React.FC<WorkItemDrawerActionsMenuProps> = ({
    open,
    anchorEl,
    onClose,
    saving,
    isDoneState,
    isTriageState,
    hasActiveSnooze,
    isArchivedState,
    hasDocument,
    sourceDocumentState,
    onRunAction,
    onReopen,
    onMarkDone,
    onStart,
    onDismiss,
    onUnsnooze,
    onSnooze,
    onClassifyApplication,
    onClassifyTask,
    onOpenInWorkbench,
    onOpenSourceDocument,
    onToggleArchive,
    onRequestDelete,
    onRerunEnrichment,
    enrichRerunBusy,
    aiCreditsBootstrapBlockingLoad,
    aiCreditsBootstrapError,
    aiCreditsEntitlements,
    showSourceRuleActions = false,
    currentSourceRuleAttention = null,
    onKeepAsTask,
    onWatchSource,
    onIgnoreSource,
    autoAdvanceEnabled,
    onToggleAutoAdvance,
    blockComplete = false,
    hideTriageVerbs = blockComplete,
    allowReopen = true,
    sourceDocumentOnly = false,
}) => {
    const showReopen = isDoneState && !isArchivedState && allowReopen;
    const showStart = isTriageState && !isArchivedState && !hideTriageVerbs;
    const showMarkComplete = !isTriageState && !isDoneState && !isArchivedState && !blockComplete;
    const showPrimary = showReopen || showStart || showMarkComplete;
    const showSchedule = !isDoneState && !isArchivedState;
    const renderMenuLabel = (icon: IconProp, label: string, color?: string) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, color: color || 'inherit' }}>
            <FontAwesomeIcon icon={icon} style={{ fontSize: '0.78rem' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
                {label}
            </Typography>
        </Box>
    );

    const menuSx = { zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 2 };
    const menuPaperProps = {
        sx: {
            minWidth: 252,
            maxWidth: 'min(360px, calc(100vw - 24px))',
            zIndex: (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 3,
            bgcolor: 'var(--color-bg-secondary)',
            color: 'var(--color-text-primary)',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-lg)',
            borderRadius: 2,
        },
    };

    const openSourceDocumentItem = hasDocument && (
        <MenuItem
            onClick={() => onRunAction(onOpenSourceDocument)}
            disabled={saving || sourceDocumentState !== 'available'}
        >
            {renderMenuLabel(faFileAlt, 'Open source document')}
        </MenuItem>
    );

    if (sourceDocumentOnly) {
        return (
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                MenuListProps={{ sx: { '& .MuiMenuItem-root': { minHeight: 44 } } }}
                sx={menuSx}
                PaperProps={menuPaperProps}
            >
                {openSourceDocumentItem}
            </Menu>
        );
    }

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            MenuListProps={{ sx: { '& .MuiMenuItem-root': { minHeight: 44 } } }}
            sx={menuSx}
            PaperProps={menuPaperProps}
        >
            {showPrimary && (
                <ListSubheader disableSticky sx={{ bgcolor: 'transparent', color: 'var(--color-text-muted)', fontSize: '0.7rem', letterSpacing: '0.08em', lineHeight: '2em' }}>
                    PRIMARY
                </ListSubheader>
            )}
            {showReopen && (
                <MenuItem onClick={() => onRunAction(onReopen)} disabled={saving}>
                    {renderMenuLabel(faUndo, 'Reopen')}
                </MenuItem>
            )}
            {showStart && (
                <MenuItem onClick={() => onRunAction(onStart)} disabled={saving}>
                    {renderMenuLabel(faPlay, 'Start')}
                </MenuItem>
            )}
            {showMarkComplete && (
                <MenuItem onClick={() => onRunAction(onMarkDone)} disabled={saving}>
                    {renderMenuLabel(faCheck, 'Mark complete')}
                </MenuItem>
            )}
            {showPrimary && showSchedule && <Divider sx={{ my: 0.5 }} />}
            {/* Snoozing finished or archived work changes nothing the user does next. */}
            {showSchedule && [
                <ListSubheader key="schedule-label" disableSticky sx={{ bgcolor: 'transparent', color: 'var(--color-text-muted)', fontSize: '0.7rem', letterSpacing: '0.08em', lineHeight: '2em' }}>
                    SCHEDULE
                </ListSubheader>,
                hasActiveSnooze && (
                    <MenuItem key="schedule-unsnooze" onClick={() => onRunAction(onUnsnooze)} disabled={saving}>
                        {renderMenuLabel(faUndo, 'Unsnooze')}
                    </MenuItem>
                ),
                <MenuItem key="schedule-1d" onClick={() => onRunAction(() => onSnooze(1))} disabled={saving} data-testid="WB_DRAWER_SNOOZE_1D">
                    {renderMenuLabel(faClock, 'Snooze 1 day')}
                </MenuItem>,
                <MenuItem key="schedule-3d" onClick={() => onRunAction(() => onSnooze(3))} disabled={saving} data-testid="WB_DRAWER_SNOOZE_3D">
                    {renderMenuLabel(faClock, 'Snooze 3 days')}
                </MenuItem>,
                <MenuItem key="schedule-7d" onClick={() => onRunAction(() => onSnooze(7))} disabled={saving} data-testid="WB_DRAWER_SNOOZE_7D">
                    {renderMenuLabel(faClock, 'Snooze 1 week')}
                </MenuItem>,
            ]}

            {isTriageState && [
                <Divider key="classification-divider" sx={{ my: 0.5 }} />,
                <ListSubheader key="classification-label" disableSticky sx={{ bgcolor: 'transparent', color: 'var(--color-text-muted)', fontSize: '0.7rem', letterSpacing: '0.08em', lineHeight: '2em' }}>
                    CLASSIFICATION
                </ListSubheader>,
                <MenuItem key="classification-application" onClick={() => onRunAction(onClassifyApplication)} disabled={saving}>
                    {renderMenuLabel(faBriefcase, 'Classify as Application')}
                </MenuItem>,
                <MenuItem key="classification-task" onClick={() => onRunAction(onClassifyTask)} disabled={saving}>
                    {renderMenuLabel(faCheckSquare, 'Classify as Task')}
                </MenuItem>,
            ]}

            {showSourceRuleActions && onKeepAsTask && onWatchSource && onIgnoreSource && [
                <Divider key="source-divider" sx={{ my: 0.5 }} />,
                <ListSubheader key="source-label" disableSticky sx={{ bgcolor: 'transparent', color: 'var(--color-text-muted)', fontSize: '0.7rem', letterSpacing: '0.08em', lineHeight: '2em' }}>
                    SOURCE
                </ListSubheader>,
                <MenuItem
                    key="source-keep"
                    onClick={() => onRunAction(onKeepAsTask)}
                    disabled={saving || currentSourceRuleAttention === 'action'}
                >
                    {renderMenuLabel(faThumbtack, 'Keep as task')}
                </MenuItem>,
                <MenuItem
                    key="source-watch"
                    onClick={() => onRunAction(onWatchSource)}
                    disabled={saving || currentSourceRuleAttention === 'watch'}
                >
                    {renderMenuLabel(faEye, 'Watch source')}
                </MenuItem>,
                <MenuItem
                    key="source-ignore"
                    onClick={() => onRunAction(onIgnoreSource)}
                    disabled={saving || currentSourceRuleAttention === 'ignore'}
                >
                    {renderMenuLabel(faBan, 'Ignore source')}
                </MenuItem>,
            ]}

            <Divider sx={{ my: 0.5 }} />
            <ListSubheader disableSticky sx={{ bgcolor: 'transparent', color: 'var(--color-text-muted)', fontSize: '0.7rem', letterSpacing: '0.08em', lineHeight: '2em' }}>
                OTHER
            </ListSubheader>
            {/* Credits usage rides along as a compact hint on the action that consumes a
                credit, instead of a tall reference block in the middle of the menu. */}
            <MenuItem
                onClick={() => onRunAction(onRerunEnrichment)}
                disabled={saving || enrichRerunBusy}
                data-testid="WB_DRAWER_RERUN_ENRICHMENT"
                sx={{ whiteSpace: 'normal' }}
            >
                <Box sx={{ display: 'grid', gap: 0.25, width: '100%' }}>
                    {renderMenuLabel(faArrowsRotate, 'Re-run enrichment')}
                    <Box
                        data-testid="WB_DRAWER_AI_CREDITS_SUMMARY"
                        sx={{ pl: '1.9rem' }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <AiMonthlyCreditsSummary
                            variant="drawer_hint"
                            bootstrapBlockingLoad={aiCreditsBootstrapBlockingLoad}
                            bootstrapError={aiCreditsBootstrapError}
                            entitlements={aiCreditsEntitlements}
                        />
                        <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', lineHeight: 1.35, display: 'block' }}>
                            Uses 1 monthly AI credit.
                        </Typography>
                    </Box>
                </Box>
            </MenuItem>
            {onOpenInWorkbench && (
                <MenuItem onClick={() => onRunAction(onOpenInWorkbench)} disabled={saving}>
                    {renderMenuLabel(faExternalLinkAlt, 'Open in Workbench')}
                </MenuItem>
            )}
            {openSourceDocumentItem}
            {isTriageState && !hideTriageVerbs && (
                <MenuItem onClick={() => onRunAction(onDismiss)} disabled={saving}>
                    {renderMenuLabel(faTimes, 'Dismiss')}
                </MenuItem>
            )}
            <MenuItem onClick={() => onRunAction(onToggleArchive)} disabled={saving}>
                {renderMenuLabel(faArchive, isArchivedState ? 'Unarchive' : 'Archive')}
            </MenuItem>
            {autoAdvanceEnabled !== undefined && onToggleAutoAdvance && (
                <MenuItem
                    role="menuitemcheckbox"
                    aria-checked={autoAdvanceEnabled}
                    onClick={() => onRunAction(onToggleAutoAdvance)}
                    data-testid="WB_DRAWER_AUTO_ADVANCE_TOGGLE"
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Box component="span" sx={{ width: '0.78rem', display: 'inline-flex', justifyContent: 'center' }}>
                            {autoAdvanceEnabled && <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.78rem' }} />}
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Auto-advance after triage actions
                        </Typography>
                    </Box>
                </MenuItem>
            )}

            <Divider sx={{ my: 0.5 }} />
            <ListSubheader disableSticky sx={{ bgcolor: 'transparent', color: 'var(--color-danger)', fontSize: '0.7rem', letterSpacing: '0.08em', lineHeight: '2em' }}>
                DANGER
            </ListSubheader>
            <MenuItem
                onClick={() => {
                    onClose();
                    onRequestDelete();
                }}
                disabled={saving}
                sx={{ color: 'var(--color-danger)' }}
            >
                {renderMenuLabel(faTrash, 'Delete', 'var(--color-danger)')}
            </MenuItem>
        </Menu>
    );
};

export default WorkItemDrawerActionsMenu;
